import {
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';

import { useTheme } from '@/shared/context/ThemeContext';
import styles from './TurnstileGate.module.css';

declare global {
  interface Window {
    turnstile?: {
      render: (
        element: HTMLElement,
        options: {
          sitekey: string;
          theme?: 'light' | 'dark' | 'auto';
          callback?: (token: string) => void;
          'error-callback'?: () => void;
          'expired-callback'?: () => void;
        }
      ) => string;

      remove: (widgetId: string) => void;
    };
  }
}

interface TurnstileGateProps {
  children: ReactNode;
}

export default function TurnstileGate({
  children,
}: TurnstileGateProps) {
  const containerRef = useRef<HTMLDivElement>(null);

  const [verified, setVerified] = useState(false);
  const [error, setError] = useState(false);
  // Mismo tema que el resto del sitio desde el arranque (ThemeContext ya
  // resolvió la preferencia del dispositivo en este punto, porque
  // ThemeProvider envuelve a TurnstileGate en App.tsx).
  const { theme } = useTheme();

  useEffect(() => {
    let widgetId: string | undefined;
    let cancelled = false;

    const renderTurnstile = () => {
      if (
        cancelled ||
        !containerRef.current ||
        !window.turnstile
      ) {
        return;
      }

      widgetId = window.turnstile.render(
        containerRef.current,
        {
          sitekey: import.meta.env.VITE_TURNSTILE_SITE_KEY,

          theme,

          callback: async (token: string) => {
            try {
              const leadsApiUrl = import.meta.env.VITE_LEADS_API_URL ?? 'http://localhost:5199';

              const response = await fetch(`${leadsApiUrl}/turnstile/verify`, {
                method: 'POST',

                headers: {
                  'Content-Type': 'application/json',
                },

                body: JSON.stringify({
                  token,
                }),
              });

              if (!response.ok) {
                throw new Error('Turnstile verification failed');
              }

              const result = await response.json();

              if (result.success) {
                setVerified(true);
              } else {
                setError(true);
              }
            } catch (err) {
              console.error(err);
              setError(true);
            }
          },

          'error-callback': () => {
            setError(true);
          },

          'expired-callback': () => {
            setVerified(false);
          },
        }
      );
    };

    /*
     * El script de Cloudflare puede todavía estar cargando.
     * Esperamos hasta que window.turnstile exista.
     */
    const interval = window.setInterval(() => {
      if (window.turnstile) {
        window.clearInterval(interval);
        renderTurnstile();
      }
    }, 100);

    return () => {
      cancelled = true;

      window.clearInterval(interval);

      if (widgetId && window.turnstile) {
        window.turnstile.remove(widgetId);
      }
    };
  }, [theme]);

  /*
   * Hasta que Cloudflare + backend verifiquen al visitante,
   * NO renderizamos la aplicación.
   */
  if (!verified) {
    return (
      <main className={styles.gate}>
        <div className={styles.card}>
          <div className={styles.logo}>
            ReInvent-X
          </div>

          <h1 className={styles.title}>
            Verificando acceso
          </h1>

          <p className={styles.description}>
            Estamos comprobando que la conexión sea segura.
          </p>

          <div
            ref={containerRef}
            className={styles.turnstile}
          />

          {error && (
            <p className={styles.error}>
              No pudimos verificar el acceso.
              Actualiza la página para intentarlo nuevamente.
            </p>
          )}
        </div>
      </main>
    );
  }

  return <>{children}</>;
}